import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { signOutGoogle } from '../utils/googleAuth';
import { useAuth } from '../AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getEffectiveAccountId, getCurrentUser, addScopeChangeListener } from '../services/authorizationService';
import { getUserNotifications, markNotificationAsRead } from '../services/notesService';
import { 
  House, 
  ChatCircle, 
  ChartBar, 
  Users, 
  FileText, 
  Gear, 
  SignOut,
  AddressBook,
  Building,
  UserGear,
  List,
  X,
  XCircle,
  Shield,
  At
} from '@phosphor-icons/react';
import AccountSwitcher from '../components/AccountSwitcher';

// Mock search data - this would typically come from your API
const searchData = [
  // Pages/Navigation
  { id: 1, type: 'page', title: 'Overview', description: 'Dashboard overview with key metrics', path: '.' },
  { id: 2, type: 'page', title: 'Feedback & Trust Hub', description: 'Manage customer reviews and responses', path: 'feedback' },
  { id: 3, type: 'page', title: 'SEO Reports', description: 'View search engine optimization analytics', path: 'seo' },
  { id: 4, type: 'page', title: 'Social Media', description: 'Manage social media content and posting', path: 'social' },
  { id: 5, type: 'page', title: 'Shift Posters', description: 'Marketing materials and promotional content', path: 'posters' },
  { id: 6, type: 'page', title: 'Account Settings', description: 'Manage your account preferences', path: 'settings' },
  
  // Reviews
  { id: 7, type: 'review', title: 'Drew Cano Review', description: '5-star review from 3 days ago', path: 'feedback' },
  { id: 8, type: 'review', title: 'Sarah Johnson Review', description: '4-star review from 5 days ago', path: 'feedback' },
  { id: 9, type: 'review', title: 'Mike Chen Review', description: '5-star review from 1 week ago', path: 'feedback' },
  
  // Actions
  { id: 10, type: 'action', title: 'Respond to Reviews', description: 'Reply to customer feedback', path: 'feedback' },
  { id: 11, type: 'action', title: 'Create Social Post', description: 'Schedule new social media content', path: 'social' },
  { id: 12, type: 'action', title: 'Download Posters', description: 'Get marketing materials', path: 'posters' },
  
  // Settings
  { id: 13, type: 'setting', title: 'Profile Settings', description: 'Update your profile information', path: 'settings' },
  { id: 14, type: 'setting', title: 'Notification Preferences', description: 'Manage notification settings', path: 'settings' },
  { id: 15, type: 'setting', title: 'Google Business Profile', description: 'Connect your Google Business account', path: 'settings' },
  
  // Marketing Materials & Posters
  { id: 16, type: 'poster', title: 'Heart Health Awareness Poster', description: 'Promote cardiovascular health awareness with prevention tips and guidelines', path: 'posters' },
  { id: 17, type: 'poster', title: 'Flu Shot Campaign Flyer', description: 'Encourage flu vaccination with eye-catching flyer for community distribution', path: 'posters' },
  { id: 18, type: 'poster', title: 'Pediatric Care Social Media Ad', description: 'Social media advertisement highlighting pediatric services for Facebook', path: 'posters' },
  { id: 19, type: 'poster', title: 'Mental Health Support Poster', description: 'Promote mental health awareness and support services with helpline numbers', path: 'posters' },
  { id: 20, type: 'poster', title: 'Diabetes Management Flyer', description: 'Comprehensive guide for diabetes management and lifestyle recommendations', path: 'posters' },
  { id: 21, type: 'poster', title: 'Team Building Social Media Ad', description: 'Highlight team expertise and collaborative approach to healthcare', path: 'posters' },
  
  // Social Media Posts
  { id: 22, type: 'social', title: 'Cardiovascular Wellness Seminar', description: 'Facebook post about monthly health seminar on cardiovascular wellness and prevention strategies', path: 'social' },
  { id: 23, type: 'social', title: 'Mental Health Awareness', description: 'Instagram post promoting mental health awareness and self-care with hashtags', path: 'social' },
  { id: 24, type: 'social', title: 'Cardiac Research Update', description: 'LinkedIn post about new research on minimally invasive cardiac procedures', path: 'social' },
  { id: 25, type: 'social', title: 'Flu Shot Reminder', description: 'X post reminding about flu season and walk-in clinic availability', path: 'social' },
  
  // Files & Components
  { id: 26, type: 'file', title: 'Overview.js', description: 'Main dashboard overview component', path: '.' },
  { id: 27, type: 'file', title: 'ReviewsDashboard.js', description: 'Reviews management dashboard', path: 'feedback' },
  { id: 28, type: 'file', title: 'NoReviewsView.js', description: 'Empty state for reviews section', path: 'feedback' },
  { id: 29, type: 'file', title: 'SocialMedia.js', description: 'Social media management interface', path: 'social' },
  { id: 30, type: 'file', title: 'SeoReports.js', description: 'SEO analytics and reports', path: 'seo' },
  { id: 31, type: 'file', title: 'Posters.js', description: 'Marketing materials and posters', path: 'posters' },
  { id: 32, type: 'file', title: 'AccountSettings.js', description: 'User account configuration', path: 'settings' },
  { id: 33, type: 'file', title: 'DashboardLayout.js', description: 'Main layout wrapper component', path: '.' },
  { id: 34, type: 'file', title: 'App.js', description: 'Root application component', path: '.' },
  { id: 35, type: 'file', title: 'firebase.js', description: 'Firebase configuration and setup', path: 'settings' },
  { id: 36, type: 'file', title: 'PostModal.js', description: 'Social media post details modal', path: 'social' },
  { id: 37, type: 'file', title: 'CalendarView.js', description: 'Calendar view for social posts', path: 'social' },
  { id: 38, type: 'file', title: 'ListView.js', description: 'List view for social posts', path: 'social' },
  { id: 39, type: 'file', title: 'LoadingSpinner.js', description: 'Reusable loading component', path: '.' },
  { id: 40, type: 'file', title: 'ErrorBoundary.js', description: 'Error handling wrapper component', path: '.' },
];

// Regular navigation items (when account is selected)
const regularNavItems = [
  { name: 'Overview', path: '.', icon: House },
  { name: 'Feedback & Trust Hub', path: 'feedback', icon: ChatCircle },
  { name: 'SEO Reports', path: 'seo', icon: ChartBar },
  { name: 'Social Media', path: 'social', icon: Users },
  { name: 'Shift Posters', path: 'posters', icon: FileText },
  { name: 'Directory', path: 'directory', icon: AddressBook },
];

// Admin navigation items (when no account is selected)
const adminNavItems = [
  { name: 'Accounts', path: 'accounts', icon: Building },
  { name: 'Users', path: 'users', icon: UserGear },
  { name: 'Migration', path: 'migration', icon: Shield },
];

const bottomItems = [
  { name: 'Account Settings', path: 'settings', icon: Gear },
];

// Removed mock notifications - now using real data from state

export default function DashboardLayout() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { canAccessDashboard, subscription, getSubscriptionStatus } = useSubscription();
  
  // Check if user is admin
  const userWithRole = getCurrentUser();
  const isAdmin = currentUser?.role === 'ADMIN' || userWithRole?.role === 'ADMIN';
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [navItems, setNavItems] = useState(regularNavItems);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Load notifications and count
  const loadNotifications = useCallback(async () => {
    if (!currentUser?.email) return;
    
    try {
      const notifs = await getUserNotifications(currentUser.email);
      setNotifications(notifs);
      const unread = notifs.filter(n => !n.isRead).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [currentUser]);

  // Load notifications on mount and periodically
  useEffect(() => {
    if (currentUser?.email) {
      loadNotifications();
      
      // Refresh every 30 seconds
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser, loadNotifications]);

  // Handle notification click
  const handleNotificationClick = useCallback(async (notification) => {
    // Mark as read immediately in local state for instant feedback
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Close the dropdown
    setShowNotifications(false);
    
    // Mark as read in Firestore
    try {
      await markNotificationAsRead(notification.id);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Revert local state if Firestore update fails
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, isRead: false } : n)
      );
      setUnreadCount(prev => prev + 1);
    }
    
    if (notification.type === 'note_tag' && notification.postId) {
      // Navigate to social media page with post ID in state
      navigate('/dashboard/social', { state: { openPostId: notification.postId } });
    }
  }, [navigate]);

  // Format time for notifications
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notifications-container')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  // Determine which navigation items to show based on user role and account scope
  useEffect(() => {
    if (currentUser) {
      const user = getCurrentUser();
      const effectiveAccountId = getEffectiveAccountId();
      
      if (user?.role === 'ADMIN' && !effectiveAccountId) {
        // Admin with no account selected - show admin navigation
        setNavItems(adminNavItems);
      } else if (user?.role === 'ADMIN') {
        // Admin viewing a client account - always show full navigation
        setNavItems(regularNavItems);
      } else {
        // Regular clients - hide restricted links for suspended accounts
        if (canAccessDashboard()) {
          setNavItems(regularNavItems);
        } else {
          // For suspended accounts, only show Account Settings
          setNavItems([]);
        }
      }
    }
  }, [currentUser, canAccessDashboard]);

  // Listen for account scope changes
  useEffect(() => {
    const unsubscribe = addScopeChangeListener((scopeData) => {
      console.log('Account scope changed in DashboardLayout:', scopeData);
      
      // Update navigation items based on new scope
      if (currentUser) {
        const user = getCurrentUser();
        const effectiveAccountId = getEffectiveAccountId();
        
        if (user?.role === 'ADMIN' && !effectiveAccountId) {
          // Admin with no account selected - show admin navigation
          setNavItems(adminNavItems);
        } else if (user?.role === 'ADMIN') {
          // Admin viewing a client account - always show full navigation
          setNavItems(regularNavItems);
        } else {
          // Regular clients - check subscription status
          if (canAccessDashboard()) {
            setNavItems(regularNavItems);
          } else {
            setNavItems([]);
          }
        }
      }
    });

    return unsubscribe;
  }, [currentUser, canAccessDashboard]);

  const handleLogout = async () => {
    console.log('Starting logout process...');
    try {
      // Sign out from Firebase (if user is signed in with Firebase)
      try {
        await signOut(auth);
        console.log('Firebase sign out successful');
      } catch (error) {
        console.log('Firebase sign out error (expected if not signed in with Firebase):', error);
      }
      
      // Sign out from Google Identity Services
      console.log('Signing out from Google...');
      signOutGoogle();
      console.log('Google sign out completed');
      
      // Small delay to ensure logout process completes
      setTimeout(() => {
        console.log('Navigating to login page...');
        navigate('/');
      }, 100);
    } catch (error) {
      console.error('Logout error:', error);
      // Still navigate to login page even if there's an error
      navigate('/');
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (query.trim().length === 0) {
      setShowSearchResults(false);
      setSearchResults([]);
      return;
    }

    // Simple local search through the search data
    const queryLower = query.toLowerCase();
    const filtered = searchData.filter(item => {
      const searchableText = [
        item.title.toLowerCase(),
        item.description.toLowerCase()
      ].join(' ');
      
      return searchableText.includes(queryLower);
    }).slice(0, 8); // Limit to 8 results

    setSearchResults(filtered);
    setShowSearchResults(true);
  };

  const handleSearchResultClick = (result) => {
    setSearchQuery('');
    setShowSearchResults(false);
    navigate(result.path);
  };

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const getSearchResultIcon = (type) => {
    switch (type) {
      case 'page':
        return (
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      case 'review':
        return (
          <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </div>
        );
      case 'action':
        return (
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        );
      case 'setting':
        return (
          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        );
      case 'file':
        return (
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      case 'poster':
        return (
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        );
      case 'social':
        return (
          <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2M9 8h6m-6 4h6m-6 4h6" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Fixed height, no scroll */}
      <div className={`
        w-64 bg-trust text-calm flex flex-col justify-between p-4 h-screen
        fixed lg:relative z-50 lg:z-auto
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div>
          {/* Mobile Close Button */}
          <div className="lg:hidden flex justify-end mb-4">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Logo */}
          <div className="mb-8">
            <svg width="129" height="47" viewBox="0 0 129 47" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M98.1553 37.7881C98.5299 37.7881 98.8673 37.825 99.167 37.8809C99.4668 37.9368 99.7484 38.0306 100.011 38.1611C100.273 38.273 100.516 38.4221 100.741 38.5898C100.966 38.7576 101.191 38.9248 101.397 39.1299L100.947 39.5771C100.76 39.4095 100.573 39.2418 100.386 39.0928C100.18 38.9436 99.9729 38.813 99.748 38.7012C99.5233 38.5894 99.2798 38.515 99.0176 38.4404C98.7552 38.3845 98.4551 38.3477 98.1553 38.3477C97.6494 38.3477 97.1995 38.4406 96.7686 38.627C96.3376 38.8134 95.9629 39.0746 95.6631 39.4102C95.3447 39.7456 95.1012 40.1368 94.9326 40.584C94.7453 41.0313 94.6699 41.5162 94.6699 42.0381C94.6699 42.5786 94.7639 43.0633 94.9512 43.5293C95.1385 43.9766 95.3818 44.3872 95.7002 44.7227C96.0188 45.0582 96.3944 45.3193 96.8066 45.5244C97.2376 45.7108 97.7063 45.8232 98.1934 45.8232C98.7927 45.8232 99.3171 45.7108 99.7666 45.4873C100.216 45.2636 100.647 44.9462 101.06 44.5361L101.473 44.9463C101.248 45.1513 101.023 45.3567 100.779 45.5244C100.536 45.7108 100.292 45.8598 100.029 45.9717C99.7669 46.1022 99.4668 46.1959 99.167 46.2705C98.8485 46.345 98.5112 46.3818 98.1553 46.3818L97.9141 46.377C97.4015 46.3525 96.9299 46.2426 96.4873 46.0469C95.9814 45.8232 95.5318 45.5243 95.1758 45.1328C94.8011 44.7414 94.5196 44.2943 94.3135 43.7725C94.1074 43.2506 93.9952 42.6911 93.9951 42.0947C93.9951 41.4795 94.1073 40.938 94.3135 40.416C94.5196 39.8942 94.8198 39.4471 95.1758 39.0557C95.5505 38.6642 95.9814 38.3654 96.4873 38.123C96.9932 37.8994 97.5557 37.7881 98.1553 37.7881ZM116.876 37.9561C117.401 37.9561 117.851 38.0306 118.244 38.1797C118.637 38.3288 118.974 38.5341 119.218 38.7764C119.424 38.9814 119.574 39.2048 119.687 39.4658C119.799 39.7268 119.855 40.0064 119.855 40.3047C119.855 40.6775 119.799 40.9949 119.668 41.2559C119.537 41.5353 119.368 41.7589 119.162 41.9639C118.937 42.1689 118.693 42.3177 118.394 42.4482C118.094 42.5787 117.775 42.654 117.419 42.71L120.174 46.252H119.387L116.726 42.8213H113.99V46.252H113.372V37.9561H116.876ZM43.2871 41.7959H48.6094V37.9561H49.2275V46.2324H48.6094V42.3555H43.2871V46.2324H42.6689V37.9561H43.2871V41.7959ZM58.1289 38.5146H52.8066V41.7959H57.585V42.3369H52.8066V45.6924H58.1846V46.2324H52.1885V37.9561H58.1289V38.5146ZM68.416 46.2324H67.7422L66.6738 43.9023H61.7637L60.6963 46.2324H60.0586L63.9375 37.8809H64.5371L68.416 46.2324ZM71.1338 45.6924H76.0244V46.2324H70.5156V37.9561H71.1338V45.6924ZM82.752 38.5146H79.8477V46.2324H79.2295V38.5146H76.3242V37.9561H82.752V38.5146ZM85.5068 41.7959H90.8477V37.9561H91.4658V46.2324H90.8477V42.3555H85.5068V46.2324H84.8887V37.9561H85.5068V41.7959ZM111.291 46.2324H110.617L109.549 43.9023H104.639L103.571 46.2324H102.934L106.812 37.8809H107.412L111.291 46.2324ZM128.325 38.5146H123.003V41.7959H127.782V42.3369H123.003V45.6924H128.382V46.2324H122.385V37.9561H128.325V38.5146ZM62.0078 43.3623H66.4111L64.2188 38.5713L62.0078 43.3623ZM104.883 43.3623H109.286L107.094 38.5713L104.883 43.3623ZM114.009 42.2432H116.801C117.157 42.2432 117.476 42.2065 117.775 42.1133C118.075 42.0201 118.338 41.908 118.544 41.7402C118.75 41.5725 118.919 41.3858 119.05 41.1436C119.181 40.9012 119.237 40.64 119.237 40.3418C119.237 39.764 119.03 39.317 118.618 38.9814C118.206 38.6646 117.607 38.4961 116.857 38.4961H114.009V42.2432ZM53.4629 2.27441C55.393 2.27443 57.0606 2.53565 58.4473 3.05762C59.8339 3.5796 60.9959 4.2695 61.9141 5.1084C62.8321 5.94723 63.5259 6.89755 63.9756 7.94141C64.4253 8.98529 64.7063 10.0294 64.7812 11.0732H59.291C58.9536 8.40758 57.0044 7.06547 53.4629 7.06543C51.9263 7.06543 50.6707 7.32657 49.6963 7.86719C48.7219 8.40777 48.2344 9.19052 48.2344 10.2344C48.2344 10.8122 48.3657 11.2971 48.6279 11.6699C48.8903 12.0614 49.3031 12.3787 49.8652 12.6396C50.4086 12.9006 51.1023 13.1426 51.9453 13.3477C52.7698 13.5527 53.7819 13.7764 54.9248 13.9814C56.1615 14.2238 57.4358 14.5228 58.71 14.877C60.0028 15.2125 61.1462 15.7157 62.1768 16.3682C63.2072 17.0206 64.0501 17.8595 64.7246 18.9219C65.3805 19.9845 65.7178 21.3456 65.7178 23.0234C65.7177 25.6892 64.7437 27.7772 62.7949 29.2686C60.8461 30.7599 58.1289 31.5244 54.6436 31.5244L54.0459 31.5146C52.5747 31.4696 51.1769 31.2657 49.8652 30.8906C48.3661 30.4619 47.0349 29.8466 45.873 29.0264C44.73 28.2061 43.7931 27.2176 43.0811 26.0059C42.369 24.8128 41.9572 23.4329 41.8447 21.9043H47.3721C47.5407 23.3955 48.1969 24.5702 49.3584 25.4277C50.5015 26.2853 52.1509 26.7334 54.2871 26.7334C55.1678 26.7334 55.9741 26.6774 56.7236 26.5469C57.4543 26.4164 58.0914 26.2112 58.6348 25.9316C59.1782 25.652 59.5906 25.2785 59.9092 24.8311C60.2275 24.3837 60.3779 23.8428 60.3779 23.1719C60.3778 22.501 60.1901 21.9228 59.8154 21.4941C59.4407 21.0654 58.9157 20.7114 58.2412 20.3945C57.5667 20.0963 56.7235 19.8165 55.7305 19.5928C54.7373 19.3504 53.6311 19.1272 52.4131 18.8662C51.2701 18.6239 50.1459 18.3257 49.0029 18.0088C47.8599 17.6732 46.8479 17.2069 45.9297 16.5918C45.0115 15.9766 44.2809 15.1745 43.7188 14.2051C43.1566 13.2357 42.875 11.9865 42.875 10.4766C42.8751 9.30232 43.1193 8.20289 43.6064 7.17773C44.0937 6.15241 44.8054 5.29434 45.7236 4.58594C46.6606 3.87757 47.7664 3.29976 49.0781 2.88965C50.3898 2.47958 51.8514 2.27441 53.4629 2.27441ZM74.9941 13.3477C75.5001 12.2106 76.3055 11.3155 77.4297 10.6631C78.5353 9.99196 79.7727 9.67578 81.1406 9.67578C83.5766 9.67585 85.5069 10.5146 86.8936 12.1924C88.28 13.8701 88.9736 16.2188 88.9736 19.2012V31.5244H83.6143V19.2012C83.6142 17.8779 83.2951 16.7784 82.6582 15.9023C82.0211 15.0262 80.9532 14.5968 79.4355 14.5967C77.9177 14.5967 76.8863 15.0253 76.1367 15.8828C75.3872 16.7403 75.0128 17.8591 75.0127 19.2012V31.5244H69.6533V0H74.9941V13.3477ZM98.7549 31.5244H93.3955V9.65625H98.7549V31.5244ZM122.385 2.27441C123.265 2.27441 123.959 2.98251 123.959 3.83984V9.69434H128.737V13.8701H123.959V22.9678C123.959 24.3843 124.241 25.1484 124.803 25.8008C125.365 26.4532 126.227 26.7705 127.407 26.7705C127.67 26.7705 127.932 26.7707 128.194 26.7334C128.457 26.7148 128.719 26.6585 128.981 26.6025V31.2627C128.663 31.3186 128.325 31.3747 127.932 31.4307C127.557 31.4866 127.164 31.5244 126.808 31.5244L126.335 31.5156C123.913 31.4304 122.035 30.7049 120.718 29.3242C119.312 27.8516 118.6 25.913 118.6 22.9678V13.8887H115.752V9.71289H118.6V2.27441H122.385ZM111.779 0C112.21 2.65136e-05 112.622 0.0368837 113.034 0.0927734C113.446 0.148676 113.859 0.223314 114.252 0.297852V4.79102C114.027 4.73515 113.784 4.69742 113.521 4.66016C113.259 4.62291 113.016 4.62305 112.791 4.62305C111.704 4.62305 110.805 4.94054 110.093 5.55566C109.362 6.17086 109.006 7.23323 109.006 8.72461V9.69434H113.54V13.8701H109.006V31.5059H103.646V13.8887H100.985V9.71289H103.627V8.78027C103.627 7.34492 103.834 6.07728 104.246 4.99609C104.658 3.91491 105.22 3.00151 105.951 2.25586C106.682 1.52882 107.525 0.969608 108.537 0.578125C109.53 0.205292 110.617 0 111.779 0ZM98.8857 7.5498H93.2646V2.25586H98.8857V7.5498Z" fill="#fff"/>
              <path d="M32.7934 9.26521V23.5266C32.7934 29.7717 27.6964 34.8424 21.4188 34.8424H21.3064V17.2255C21.3064 15.268 19.7135 13.6834 17.7459 13.6834H0C0 7.38234 5.13452 2.27435 11.4683 2.27435H25.7663C29.6453 2.27435 32.7934 5.40626 32.7934 9.26521Z" fill="#3FF1C3"/>
            </svg>
          </div>

          {/* Main Nav Links */}
          <nav className="space-y-2">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  end={item.path === '.'}
                  onClick={handleMobileNavClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive 
                        ? 'bg-vision text-calm' 
                        : 'text-fresh hover:bg-wellness hover:text-calm'
                    }`
                  }
                >
                  <IconComponent className="w-5 h-5" weight="regular" />
                  {item.name}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Bottom Nav Links */}
        <div>
          <nav className="space-y-2">
            {bottomItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  onClick={handleMobileNavClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive 
                        ? 'bg-vision text-calm' 
                        : 'text-fresh hover:bg-wellness hover:text-calm'
                    }`
                  }
                >
                  <IconComponent className="w-5 h-5" weight="regular" />
                  {item.name}
                </NavLink>
              );
            })}
            <button
              onClick={() => {
                handleMobileNavClick();
                handleLogout();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-fresh hover:bg-wellness hover:text-calm rounded-lg transition-colors"
            >
              <SignOut className="w-5 h-5" weight="regular" />
              Logout
            </button>
          </nav>
        </div>
      </div>

      {/* Page Content - Takes remaining width, scrolls independently */}
      <div className="flex-1 bg-light flex flex-col h-screen lg:ml-0">
        
        {/* Top NavBar - Fixed at top */}
        <div className="bg-calm shadow-sm border-b border-outline px-4 lg:px-6 py-4 flex items-center justify-between gap-4 flex-shrink-0">

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-600" />
            ) : (
              <List className="w-6 h-6 text-gray-600" />
            )}
          </button>

          {/* Search Bar */}
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1">
              <div className="bg-[rgba(0,0,0,0.04)] rounded-lg px-3 py-2 flex items-center">
                <svg className="w-4 h-4 text-[#104151] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchQuery.trim() && setShowSearchResults(true)}
                  onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                  className="bg-transparent border-none outline-none text-[#104151] text-[14px] flex-1 placeholder-[#104151]"
                />
                <div className="ml-auto flex items-center hidden sm:flex">
                  <div className="w-px h-4 bg-gray-300 mx-2"></div>
                  <span className="text-[#104151] text-[12px]">/</span>
                </div>
              </div>

              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  <div className="p-2">
                    <div className="text-xs font-medium text-gray-500 px-3 py-2">Search Results</div>
                    {searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleSearchResultClick(result)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                      >
                        {getSearchResultIcon(result.type)}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-sm truncate">{result.title}</div>
                          <div className="text-xs text-gray-500 truncate">{result.description}</div>
                        </div>
                        <div className="text-xs text-gray-400 capitalize">{result.type}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {showSearchResults && searchQuery.trim() && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No results found for "{searchQuery}"
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Account Switcher */}
          <div className="hidden lg:flex items-center gap-2 px-6 border-l border-r border-outline">
            <AccountSwitcher />
          </div>

          {/* Subscription Status */}
          {!isAdmin && (() => {
            const status = getSubscriptionStatus();
            const isActive = status.canAccess;
            
            // Extract period end from subscription items (Stripe API structure)
            const periodEndTimestamp = subscription?.items?.data?.[0]?.current_period_end;
            const renewalDate = periodEndTimestamp
              ? new Date(periodEndTimestamp * 1000).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
              : null;
            
            return (
              <div className="hidden xl:flex items-center gap-3 px-6 border-l border-r border-outline">
                <div className={`w-5 h-5 ${isActive ? 'bg-[#3FF1C2]' : 'bg-[#E34E44]'} rounded-full flex items-center justify-center`}>
                  <div className={`w-2 h-2 ${isActive ? 'bg-[#B1F6E7]' : 'bg-[#F5A8A3]'} rounded-full`}></div>
                </div>
                <div className="text-[#104151]">
                  <div className="text-[12px]">{isActive ? 'Subscription Active' : 'Subscription Inactive'}</div>
                  {renewalDate && (
                    <div className="text-[12px]">
                      {isActive ? `Renews on ${renewalDate}` : `Expired on ${renewalDate}`}
                    </div>
                  )}
                  {!renewalDate && (
                    <div className="text-[12px] text-gray-500">No subscription</div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Notifications */}
          <div className="flex items-center gap-2 pl-2 lg:pl-6 relative notifications-container">
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 18 20" width="18px" height="20px"><path d="M17.7927 14.2444C17.2724 13.3481 16.4989 10.8122 16.4989 7.5C16.4989 5.51088 15.7087 3.60322 14.3022 2.1967C12.8957 0.790176 10.988 0 8.99891 0C7.00979 0 5.10214 0.790176 3.69561 2.1967C2.28909 3.60322 1.49891 5.51088 1.49891 7.5C1.49891 10.8131 0.724538 13.3481 0.204226 14.2444C0.0713545 14.4722 0.000914339 14.7311 8.84153e-06 14.9949C-0.000896656 15.2586 0.0677647 15.518 0.199068 15.7467C0.330372 15.9755 0.519675 16.1656 0.747887 16.2978C0.976099 16.4301 1.23515 16.4998 1.49891 16.5H5.32485C5.49789 17.3467 5.95806 18.1077 6.62754 18.6542C7.29702 19.2007 8.1347 19.4992 8.99891 19.4992C9.86312 19.4992 10.7008 19.2007 11.3703 18.6542C12.0398 18.1077 12.4999 17.3467 12.673 16.5H16.4989C16.7626 16.4996 17.0215 16.4298 17.2496 16.2975C17.4777 16.1651 17.6669 15.975 17.7981 15.7463C17.9292 15.5176 17.9978 15.2583 17.9969 14.9946C17.9959 14.7309 17.9255 14.4722 17.7927 14.2444ZM8.99891 18C8.53375 17.9999 8.08005 17.8555 7.70029 17.5869C7.32052 17.3183 7.03335 16.9386 6.87829 16.5H11.1195C10.9645 16.9386 10.6773 17.3183 10.2975 17.5869C9.91777 17.8555 9.46408 17.9999 8.99891 18ZM1.49891 15C2.22079 13.7588 2.99891 10.8825 2.99891 7.5C2.99891 5.9087 3.63105 4.38258 4.75627 3.25736C5.88149 2.13214 7.40762 1.5 8.99891 1.5C10.5902 1.5 12.1163 2.13214 13.2416 3.25736C14.3668 4.38258 14.9989 5.9087 14.9989 7.5C14.9989 10.8797 15.7752 13.7559 16.4989 15H1.49891Z" fill="var(--fill-0, #104151)" id="Vector"></path></svg>
                {unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#E34E44] rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{unreadCount}</span>
                  </div>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                      {unreadCount > 0 && (
                        <span className="text-sm text-gray-500">{unreadCount} unread</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        <p>No notifications</p>
                        <p className="text-xs mt-1">You'll see mentions and updates here</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {notifications.map((notification) => (
                          <button
                            key={notification.id}
                            className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                              !notification.isRead ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-1">
                                {notification.type === 'note_tag' ? (
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <At className="w-4 h-4 text-blue-600" />
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className={`text-sm font-medium ${
                                    !notification.isRead ? 'text-gray-900' : 'text-gray-600'
                                  }`}>
                                    {notification.type === 'note_tag' ? 'You were mentioned' : 'Notification'}
                                  </p>
                                  <span className="text-xs text-gray-400">{formatTime(notification.createdAt)}</span>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">{notification.content}</p>
                                {notification.noteContent && (
                                  <div className="mt-1 text-xs text-gray-500 italic">"{notification.noteContent}"</div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">From: {notification.fromUser}</p>
                                {!notification.isRead && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {notifications.length > 0 && (
                    <div className="p-3 border-t border-gray-200">
                      <button 
                        onClick={() => {
                          loadNotifications();
                        }}
                        className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Refresh Notifications
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <span className="text-[#104151] text-[15px] font-bold hidden sm:block">Notifications</span>
          </div>
        </div>

        {/* Subscription Renewal Banner - Only show for clients without active subscription */}
        {!isAdmin && !canAccessDashboard() && (
          <div className="bg-red-50 border-b border-red-200 px-4 lg:px-6 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-sm text-red-800">
                  <strong>Subscription Required:</strong> Please subscribe to access all features of the Shift Healthcare Platform.
                </p>
              </div>
              <button
                onClick={() => navigate('/dashboard/settings')}
                className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
              >
                Subscribe Now
              </button>
            </div>
          </div>
        )}

        {/* Content - Scrollable area */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8">
          <Outlet />
        </div>
      </div>

    </div>
  );
}
